import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

async function callFunction<T>(name: string, body: Record<string, unknown>): Promise<T> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session) throw new Error("Musisz być zalogowany");

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
  const response = await fetch(`${supabaseUrl}/functions/v1/${name}`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });

  const data = (await response.json().catch(() => ({}))) as T & { error?: string };
  if (!response.ok) {
    throw new Error(data.error ?? "Operacja nie powiodła się");
  }
  return data;
}

interface RequestDeletionResult {
  success: boolean;
  deleted_at: string;
  deletion_scheduled_at: string;
}

/** Owner initiates org deletion (soft-delete + 30-day grace). Requires typing the org name. */
export function useRequestOrgDeletion() {
  const queryClient = useQueryClient();
  return useMutation<RequestDeletionResult, Error, { organizationId: string; orgName: string }>({
    mutationFn: ({ organizationId, orgName }) =>
      callFunction<RequestDeletionResult>("request-org-deletion", {
        organization_id: organizationId,
        org_name: orgName,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["org-access"] });
      queryClient.invalidateQueries({ queryKey: ["organization"] });
    },
  });
}

/** Owner cancels a pending deletion during the grace period. */
export function useCancelOrgDeletion() {
  const queryClient = useQueryClient();
  return useMutation<{ success: boolean }, Error, { organizationId: string }>({
    mutationFn: ({ organizationId }) =>
      callFunction<{ success: boolean }>("cancel-org-deletion", {
        organization_id: organizationId,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["org-access"] });
      queryClient.invalidateQueries({ queryKey: ["organization"] });
    },
  });
}
